// Light/dark theme. Default is light; dark is opt-in and persisted in
// localStorage. The initial `.dark` class is set by the inline no-FOUC script
// in index.html before React mounts (so there's no flash and no hydration
// mismatch); this module keeps the class + storage in sync at runtime.
//
// Exports (the PDF report) never consult this – they render off-DOM with a
// fixed light palette in src/lib/pdf.jsx, so they can't be affected by dark
// mode. See also the token definitions in src/index.css (`:root` / `.dark`).
import { useEffect, useState } from 'react'

export const THEME_STORAGE_KEY = 'retira-theme'
export const THEME_CHANGE_EVENT = 'retira-theme-change'
// Temporary product gate. The dark tokens and switching logic stay in place
// so dark mode can be restored by flipping this and the pre-paint HTML gate.
export const DARK_MODE_ENABLED = false

// Apply a theme to the document and persist it. Safe to call anytime after the
// DOM exists.
export function applyTheme(theme) {
  const isDark = DARK_MODE_ENABLED && theme === 'dark'
  const nextTheme = isDark ? 'dark' : 'light'
  const root = document.documentElement
  root.classList.toggle('dark', isDark)
  root.style.colorScheme = nextTheme
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, nextTheme)
  } catch {
    // Ignore write failures (private mode / storage disabled).
  }
  window.dispatchEvent(new CustomEvent(THEME_CHANGE_EVENT, { detail: { theme: nextTheme } }))
}

// Returns { isDark, toggle }. Initializes to light so the first client render
// matches the server-rendered markup; the real value is read from the DOM in an
// effect right after mount (the no-FOUC script has already set the class).
export function useDarkMode() {
  const [isDark, setIsDark] = useState(false)

  useEffect(() => {
    if (!DARK_MODE_ENABLED) {
      applyTheme('light')
      setIsDark(false)
      return undefined
    }

    setIsDark(document.documentElement.classList.contains('dark'))

    const syncFromDocument = () => {
      setIsDark(document.documentElement.classList.contains('dark'))
    }

    const onThemeChange = (event) => {
      if (event.detail?.theme) {
        setIsDark(event.detail.theme === 'dark')
        return
      }
      syncFromDocument()
    }

    // Keep tabs in sync when the preference changes in another tab.
    const onStorage = (event) => {
      if (event.key !== THEME_STORAGE_KEY) return
      const dark = event.newValue === 'dark'
      document.documentElement.classList.toggle('dark', dark)
      document.documentElement.style.colorScheme = dark ? 'dark' : 'light'
      setIsDark(dark)
    }
    window.addEventListener(THEME_CHANGE_EVENT, onThemeChange)
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener(THEME_CHANGE_EVENT, onThemeChange)
      window.removeEventListener('storage', onStorage)
    }
  }, [])

  // Read the DOM class (the source of truth, set by the no-FOUC script) so the
  // side effect runs exactly once per click and can't drift from React state –
  // e.g. under StrictMode's double-invoked render in dev.
  const toggle = () => {
    if (!DARK_MODE_ENABLED) return
    const next = !document.documentElement.classList.contains('dark')
    applyTheme(next ? 'dark' : 'light')
    setIsDark(next)
  }

  return { isDark, toggle }
}
